<template>
  <div class="cr-panel gd-items" :class="{'has-error': hasError}">
    <div class="cr-panel-head">
      <div class="cr-panel-title">
        <fa-icon :icon="['fal', 'list']" class="cr-panel-icon"/>
        Itens da guia
        <span class="label label-rounded">{{ selectedCount }}</span>
      </div>
      <div class="cr-panel-subtitle">
        <template v-if="readonly && status !== 'open'">
          # = sequencial do item no lote (chave de glosa e recurso)
        </template>
        <template v-else>
          Procedimentos e outras despesas com preço da tabela do convênio ·
          o preço vem sempre da regra do plano
        </template>
      </div>
    </div>
    <div class="cr-table-scroll">
      <table class="table">
        <thead>
        <tr>
          <th style="width: 30px" v-if="selectable">
            <label class="form-checkbox">
              <input type="checkbox" :checked="allSelected"
                     :indeterminate.prop="partiallySelected" @change="toggleAll">
              <i class="form-icon"></i>
            </label>
          </th>
          <th style="width: 30px">#</th>
          <th>Serviço</th>
          <th style="width: 190px">Executante</th>
          <th class="text-center" style="width: 70px">Qtde</th>
          <th class="text-center" style="width: 80px">Fator</th>
          <th class="text-right">Valor</th>
          <th style="width: 70px" v-if="!readonly"></th>
        </tr>
        </thead>
        <tbody v-for="(item, index) in items" :key="item.key">
        <tr :class="rowClass(item)">
          <td v-if="selectable">
            <label class="form-checkbox"
                   :class="{'tooltip tooltip-right': consultationLocked(item)}"
                   :data-tooltip="consultationTooltip(item)">
              <input type="checkbox" :checked="item.selected" :disabled="consultationLocked(item)"
                     @change="toggle(item)">
              <i class="form-icon"></i>
            </label>
          </td>
          <td class="text-gray gd-val">{{ item.sequencialItem || index + 1 }}</td>
          <td class="gd-item-service">
            <span class="name">{{ item.expense.name }}</span>
            <span class="gd-sub">
              {{ typeName(item.expense.type) }} · {{ item.expense.code | tuss }}
              <template v-if="item.expense.tableCode">
                · tabela {{ item.expense.tableCode }}
              </template>
              <span class="gd-kind" v-if="item.kind === 'other_expense'">Outra despesa</span>
              <template v-if="item.return"> · <span class="gd-warn-text">Retorno</span></template>
              <template v-if="transferName(item)">
                · <span class="text-gray">Repasse: {{ transferName(item) }}</span>
              </template>
              <template v-if="item.accredited"> · Credenciado</template>
              <template v-if="item.pricingError">
                · <span class="gd-err text-error">Falha ao calcular</span>
              </template>
              <template v-else-if="isPricing(item)"></template>
              <template v-else-if="!item.priced && !item.return">
                · <span class="gd-warn-text text-bold">Sem preço na tabela do convênio</span>
              </template>
            </span>
            <a class="gd-sub gd-err text-error c-hand" v-if="wayTechniqueError(index)"
               @click="toggleDetail(item, true)">
              Informe a via de acesso e a técnica
            </a>
          </td>
          <td :class="{'has-error': professionalError(index)}">
            <template v-if="readonly">{{ item.professionalName || '-' }}</template>
            <template v-else>
              <select class="form-select" :value="executantValue(item)"
                      @change="setExecutant(item, $event.target.value)"
                      @blur="touchProfessional(index)">
                <option value="">[Selecione]</option>
                <option v-for="option in professionals" :value="option.value" :key="option.value">
                  {{ option.label }}
                </option>
              </select>
              <div class="form-input-hint" v-if="professionalError(index)">Campo obrigatório</div>
            </template>
          </td>
          <td class="text-center"
              :class="{'tooltip tooltip-left': !readonly && quantityLocked(item)}"
              :data-tooltip="quantityTooltip(item)">
            <template v-if="readonly">{{ item.quantity }}</template>
            <input type="number" min="1" max="999" class="form-input text-center" v-else
                   v-model.number="item.quantity" :disabled="quantityLocked(item)"
                   @change="quantityChanged(item)">
          </td>
          <td class="text-center">
            <template v-if="readonly">{{ factorLabel(item.factor) }}</template>
            <template v-else>
              <select class="form-select" v-model.number="item.factor"
                      @change="$emit('factor', item)">
                <option v-for="option in factorOptions(item)" :value="option.value"
                        :key="option.value">{{ option.label }}</option>
              </select>
              <span class="gd-sub gd-reduction tooltip tooltip-left" v-if="reductionText(item)"
                    :data-tooltip="reductionTooltip(item)">{{ reductionText(item) }}</span>
            </template>
          </td>
          <td class="text-right gd-val" :class="valueClass(item)">
            <span class="loading" v-if="isPricing(item)"></span>
            <fa-icon class="text-error" :icon="['fal', 'lock']"
                     v-else-if="hideInsuranceValue && !item.return"/>
            <template v-else>
              {{ item.value.insurance | currency }}
              <span class="unit" v-if="item.quantity > 1 && !item.return">
                unit. {{ item.unit.insurance | currency }}
              </span>
            </template>
          </td>
          <td class="text-right text-nowrap" v-if="!readonly">
            <button class="btn btn-sm btn-action btn-icon btn-neutral tooltip tooltip-left"
                    data-tooltip="Recalcular preço" v-if="item.pricingError || !item.priced"
                    :disabled="isPricing(item)" @click="$emit('change', item)">
              <fa-icon :icon="['fal', 'redo']"/>
            </button>
            <button class="btn btn-sm btn-action btn-icon btn-neutral tooltip tooltip-left" v-else
                    :data-tooltip="expanded.includes(item.key) ? 'Ocultar detalhes' : 'Detalhes'"
                    @click="toggleDetail(item)">
              <fa-icon :icon="['fal', detailIcon(item)]"/>
            </button>
            <button class="btn btn-sm btn-action btn-icon btn-neutral text-error
                           tooltip tooltip-left"
                    :data-tooltip="selectable ? 'Deixar fora desta guia' : 'Remover da guia'"
                    :disabled="removing === item.key" :class="{loading: removing === item.key}"
                    @click="$emit('remove', item)">
              <fa-icon :icon="['fal', 'times']"/>
            </button>
          </td>
        </tr>
        <tr class="gd-item-detail" v-if="expanded.includes(item.key)">
          <td :colspan="selectable ? 8 : 7">
            <guide-item-detail :item="item" :v="professionalNode(index)"
                               :professionals="professionals" :readonly="readonly"
                               @change="$emit('change', item)"/>
          </td>
        </tr>
        </tbody>
        <tbody v-if="items.length === 0">
        <tr>
          <td :colspan="selectable ? 8 : 7" class="text-center text-gray">
            Nenhum item nesta guia
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    <div class="gd-items-error" v-if="hasError">{{ errorMessage }}</div>
    <div class="gd-items-add" v-if="allowAdd && !readonly">
      <button class="btn btn-sm btn-primary btn-icon btn-icon-left"
              :class="{loading: adding}" :disabled="adding" @click="$emit('add')">
        <fa-icon :icon="['fal', 'plus']"/>Adicionar item
      </button>
      <span class="hint">
        Itens pendentes deste agendamento · o item desmarcado fica pendente para outra guia
      </span>
    </div>
  </div>
</template>

<script>
import { getName as expenseTypeName } from '@/data/expense-types';
import GuideItemDetail from './GuideItemDetail.vue';

export default {
  components: {
    GuideItemDetail,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    v: {
      type: Object,
      default: null,
    },
    professionals: {
      type: Array,
      default: () => [],
    },
    reducers: {
      type: Array,
      default: () => [{ value: 1, label: '100%' }],
    },
    status: {
      type: String,
      default: 'open',
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    allowAdd: {
      type: Boolean,
      default: false,
    },
    adding: {
      type: Boolean,
      default: false,
    },
    removing: {
      type: String,
      default: null,
    },
    pricingKeys: {
      type: Array,
      default: () => [],
    },
    hideInsuranceValue: {
      type: Boolean,
      default: false,
    },
    singleConsultation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expanded: [],
    };
  },
  computed: {
    selectedCount() {
      return this.items.filter(item => item.selected).length;
    },
    allSelected() {
      return this.items.length > 0
        && this.items.every(item => item.selected || this.consultationLocked(item));
    },
    selectedConsultation() {
      if (!this.singleConsultation) return null;
      return this.items.find(item => item.selected && this.isConsultation(item)) || null;
    },
    singleConsultationMessage() {
      return 'A guia SP/SADT aceita no máximo uma consulta';
    },
    partiallySelected() {
      return this.selectedCount > 0 && !this.allSelected;
    },
    hasError() {
      return !!(this.v && this.v.$error);
    },
    errorMessage() {
      return this.v && this.v.singleConsultation
        ? this.singleConsultationMessage
        : 'Inclua pelo menos um item na guia';
    },
  },
  methods: {
    typeName(type) {
      return expenseTypeName(type) || type;
    },
    isConsultation(item) {
      return item.expense.type === 'consultation';
    },
    consultationTooltip(item) {
      return this.consultationLocked(item) ? this.singleConsultationMessage : null;
    },
    quantityTooltip(item) {
      if (this.readonly || !this.quantityLocked(item)) return null;
      return this.isSplit(item)
        ? 'Quantidade desmembrada em linhas pela regra de redução'
        : 'Consulta na SP/SADT tem quantidade 1';
    },
    isSplit(item) {
      return this.items
        .filter(found => found.appointmentExpenseId === item.appointmentExpenseId).length > 1;
    },
    consultationLocked(item) {
      return this.singleConsultation && this.isConsultation(item) && !item.selected
        && !!this.selectedConsultation;
    },
    quantityLocked(item) {
      return (this.singleConsultation && this.isConsultation(item)) || this.isSplit(item);
    },
    isPricing(item) {
      return this.pricingKeys.includes(item.key);
    },
    rowClass(item) {
      return {
        unpriced: !item.priced && !item.return && !this.isPricing(item),
        unselected: this.selectable && !item.selected,
        return: item.return,
      };
    },
    valueClass(item) {
      if (item.return) return 'text-gray';
      return !item.priced && !this.isPricing(item) ? 'gd-warn-text' : '';
    },
    transferName(item) {
      if (!item.transferProfessionalId || item.transferProfessionalId === item.professionalId) {
        return null;
      }
      return item.transferProfessionalName || null;
    },
    executantValue(item) {
      return item.professionalId ? `${item.professionalId}|${item.specialtyCode || ''}` : '';
    },
    professionalNode(index) {
      const items = this.v && this.v.items ? this.v.items : null;
      return items && items.$each ? items.$each[index] : null;
    },
    professionalError(index) {
      const node = this.professionalNode(index);
      return !!(node && node.professionalId && node.professionalId.$error);
    },
    touchProfessional(index) {
      const node = this.professionalNode(index);
      if (node && node.professionalId) node.professionalId.$touch();
    },
    wayTechniqueError(index) {
      const node = this.professionalNode(index);
      if (!node) return false;
      return !!((node.way && node.way.$error) || (node.technique && node.technique.$error));
    },
    reductionText(item) {
      if (!item.reduction || item.factorEdited) return '';
      return `${item.reduction.position}º · ${this.factorLabel(item.reduction.factor)}`;
    },
    reductionTooltip(item) {
      const rule = item.reduction ? item.reduction.rule : null;
      if (!rule) return '';
      const reducers = rule.reducers || {};
      return `Redução automática · ${rule.description || rule.prefixCode}: `
        + `${reducers.first}/${reducers.second}/${reducers.third}/${reducers.others}%`;
    },
    setExecutant(item, value) {
      const [professionalId, specialtyCode] = (value || '').split('|');
      const option = this.professionals.find(found => found.value === value);
      item.professionalId = professionalId || null;
      item.specialtyCode = specialtyCode || null;
      item.professionalName = option ? option.name : null;
      this.$emit('change', item);
    },
    quantityChanged(item) {
      if (!item.quantity || item.quantity < 1) item.quantity = 1;
      this.$emit('change', item);
    },
    factorOptions(item) {
      const options = [...this.reducers];
      if (!options.some(option => option.value === item.factor)) {
        options.push({ value: item.factor, label: this.factorLabel(item.factor) });
      }
      return options;
    },
    factorLabel(factor) {
      return `${Math.round((Number(factor) || 1) * 100)}%`;
    },
    toggle(item) {
      item.selected = !item.selected;
      this.$emit('toggle', item);
    },
    toggleAll() {
      const selected = !this.allSelected;
      let consultation = selected ? this.selectedConsultation : null;
      this.items.forEach((item) => {
        if (selected && this.singleConsultation && this.isConsultation(item)) {
          if (!consultation) consultation = item;
          item.selected = item === consultation;
          return;
        }
        item.selected = selected;
      });
      this.$emit('toggle', null);
    },
    detailIcon(item) {
      return this.expanded.includes(item.key) ? 'chevron-up' : 'chevron-down';
    },
    toggleDetail(item, open = false) {
      if (this.expanded.includes(item.key)) {
        if (!open) this.expanded = this.expanded.filter(key => key !== item.key);
      } else {
        this.expanded.push(item.key);
      }
    },
  },
};
</script>
